// WHY (users screen fix, 25 Oct 2026): Edit User offers only what
// firestore.rules allows (D-305), through the shared rules mirror in
// src/utils/userRoles.js:
//   - own doc ("self"): name only - role and Active are shown, not editable
//     (the rules refuse them on your own doc; even ADDING a missing field counts)
//   - other users ("manage"): role list = roles the caller may give, incl.
//     Site Engineer (MED-29); a stored role that is not one of the 7
//     (e.g. "admin") must be replaced before Save
//   - Active tick reads ONE field; status follows it (MED-31)
//   - technician phone normalised to +91XXXXXXXXXX and checked for a
//     duplicate in the company (LOW-13)
//   - a refused save is shown directly above the button (it used to fail silently)
import React, { useEffect, useState, useCallback } from "react";
import { updateUser, getAllUsers } from "./userService";
import { useNavigate, useParams } from "react-router-dom";
import { rolesCallerMayGive, roleLabel, editModeFor, buildUserUpdate } from "../../utils/userRoles";
import { normaliseIndianMobile, normaliseWhatsAppNumber } from "../../utils/phoneIn";
import { ACCOUNTS_ACCESS_ROLES } from "../../services/purchaseBillService";
import { serverTimestamp } from "firebase/firestore";

function readCaller() {
  const u = JSON.parse(localStorage.getItem("user") || "{}");
  return { uid: u?.uid || "", role: u?.role || "" };
}

export default function EditUser() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const [caller]                = useState(readCaller);
  const [original, setOriginal] = useState(null); // the users doc as loaded
  const [form, setForm]         = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");

  const loadUser = useCallback(async () => {
    const users = await getAllUsers();
    const found = users.find((u) => u.id === id);
    if (!found) { setNotFound(true); return; }
    setOriginal(found);
    setForm({ ...found, active: found.active ?? (found.status === "active"), accounts_access: found.accounts_access === true }); // MED-31 / D-314: one field per tick
  }, [id]);

  useEffect(() => { loadUser(); }, [loadUser]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setError("");
    setForm(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  if (notFound) return <div className="p-6">User not found in your company.</div>;
  if (!form || !original) return <div className="p-6">Loading...</div>;

  // The mode comes from the doc AS LOADED - picking another role in the
  // dropdown never changes what the caller may do.
  const mode         = editModeFor(caller, { id: original.id, uid: original.uid, role: original.role });
  const roleOptions  = rolesCallerMayGive(caller.role);
  const roleListed   = roleOptions.some((o) => o.value === form.role);
  const isTechnician = form.role === "technician";
  const showPhone    = mode === "manage" && isTechnician;
  // D-314: only the Owner gives "Accounts access", and only to a manager / sm.
  const showAccessTick = caller.role === "owner" && mode === "manage" && ACCOUNTS_ACCESS_ROLES.includes(form.role);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    let phone = form.phone || "";
    if (showPhone) {
      const p = normaliseIndianMobile(phone);
      if (!p.ok)   { setError(p.error); return; }
      if (p.empty) { setError("Phone number is required."); return; }
      phone = p.phone;
    }

    // D-297: contact-only WhatsApp number (optional, every role) — never used to log in.
    const waCheck = normaliseWhatsAppNumber(form.whatsapp_phone);
    if (!waCheck.ok) { setError(waCheck.error); return; }

    const built = buildUserUpdate({ mode, form: { ...form, phone, whatsapp_phone: waCheck.phone }, callerRole: caller.role });
    if (!built.ok) { setError(built.error); return; }

    // D-314: accounts_access is saved only when it changes. It is also taken away
    // when the role no longer qualifies; syncUserClaims then signs that user out.
    const payload = { ...built.payload };
    if (caller.role === "owner" && mode === "manage") {
      const want = ACCOUNTS_ACCESS_ROLES.includes(form.role) && form.accounts_access === true;
      const had  = original.accounts_access === true;
      if (want !== had) {
        payload.accounts_access            = want;
        payload.accounts_access_changed_by = caller.uid;
        payload.accounts_access_changed_at = serverTimestamp();
      }
    }

    try {
      setSaving(true);
      // A technician's number must be unique in the company - OTP login finds
      // the doc by this number.
      if (showPhone && phone !== (original.phone || "")) {
        const users = await getAllUsers();
        if (users.some((u) => u.id !== original.id && u.phone === phone)) {
          setError("This phone number is already registered in this company.");
          return;
        }
      }
      await updateUser(id, payload);
      navigate("/users");
    } catch (err) {
      console.error("Edit User Error:", err);
      setError(err?.code === "permission-denied"
        ? "Not saved - you are not allowed to make this change."
        : `Not saved - ${err?.message || "please try again."}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Edit User</h1>

      {/* Company — read only */}
      <div className="bg-slate-50 border border-slate-200 rounded p-3 mb-4 text-sm text-slate-600">
        🏢 Company: <strong>{form.company_name || form.company_id}</strong>
      </div>

      {mode === "none" ? (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800">
          You cannot edit this user ({roleLabel(original.role)}).
        </div>
      ) : (
      <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">

        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-1">Full Name</label>
          <input
            type="text"
            name="name"
            autoComplete="off"
            className="border p-2 w-full rounded"
            value={form.name || ""}
            onChange={handleChange}
          />
        </div>

        {/* Role */}
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-1">Role</label>
          {mode === "self" ? (
            <div className="border p-2 w-full rounded bg-slate-50 text-slate-500">
              {roleLabel(original.role)}
            </div>
          ) : (
            <select
              name="role"
              className="border p-2 w-full rounded"
              value={roleListed ? form.role : ""}
              onChange={handleChange}
            >
              {!roleListed && (
                <option value="" disabled>
                  {roleLabel(original.role)} - pick a valid role
                </option>
              )}
              {roleOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          )}
        </div>

        {/* Email — read only (not editable) */}
        {!isTechnician && (
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Email Address</label>
            <input
              type="email"
              className="border p-2 w-full rounded bg-slate-50 text-slate-400 cursor-not-allowed"
              value={form.email || ""}
              readOnly
              title="Email cannot be changed"
            />
            <p className="text-xs text-slate-400 mt-1">Email cannot be changed after account creation.</p>
          </div>
        )}

        {/* Phone — technicians log in by OTP to this number */}
        {showPhone && (
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Mobile Number *</label>
            <input
              type="tel"
              name="phone"
              autoComplete="off"
              className="border p-2 w-full rounded"
              value={form.phone || ""}
              onChange={handleChange}
              placeholder="9876543210"
            />
          </div>
        )}

        {/* WhatsApp — contact only, for reminders (D-297); never used to log in. Every role, own doc too. */}
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-1">WhatsApp number (optional, for reminders)</label>
          <input type="tel" name="whatsapp_phone" autoComplete="off" placeholder="9876543210"
            className="border p-2 w-full rounded" value={form.whatsapp_phone || ""} onChange={handleChange} />
          <p className="text-xs text-slate-400 mt-1">
            Only for WhatsApp reminders from Team Activity. Never used to log in.
            {isTechnician ? " Leave empty to use the login number." : ""}
          </p>
        </div>

        {/* Active */}
        {mode === "self" ? (
          <p className="text-sm text-slate-500">You cannot change your own role or Active status.</p>
        ) : (
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              name="active"
              checked={form.active === true}
              onChange={handleChange}
            />
            Active User
          </label>
        )}

        {/* D-314: Accounts access — Owner only, manager / sm only */}
        {showAccessTick && (
          <div className="border border-slate-200 rounded p-3">
            <label className="flex items-center gap-2 text-sm text-slate-700 font-medium">
              <input
                type="checkbox"
                name="accounts_access"
                checked={form.accounts_access === true}
                onChange={handleChange}
              />
              Accounts access
            </label>
            <p className="text-xs text-slate-500 mt-1">
              Lets this user open Accounts, Payables, Cash / Bank Book and Opening Balances,
              record and reverse vendor payments, and enter opening balances. It starts when
              they log in again; removing it signs them out within an hour.
            </p>
          </div>
        )}

        {/* Error next to the button that was pressed */}
        {error && (
          <div role="alert" className="bg-red-100 text-red-600 p-3 rounded text-sm">{error}</div>
        )}

        <button
          type="submit"
          disabled={saving}
          className={`px-4 py-2 rounded w-full font-semibold text-white ${
            saving ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {saving ? "Saving..." : "Update User"}
        </button>
      </form>
      )}
    </div>
  );
}